<template>
  <div class="page-container">
    <header class="header">
      <el-button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </el-button>
      <h1 class="title">收货</h1>
      <div class="header-right">
        <div class="header-placeholder"></div>
      </div>
    </header>

    <main
      class="content"
      @click="closePalletDropdown"
      v-loading="loading"
      element-loading-text="加载中..."
    >
      <template v-if="!loading && detailData">
        <div class="step-card">
          <el-steps style="max-width: 600px" :active="currentStep" align-center>
            <el-step title="归托数" description="填写归托数" />
            <el-step title="扫托盘" description="扫描托盘码" />
            <el-step title="收货完成" description="确认收货" />
          </el-steps>
        </div>

        <div class="detail-card">
          <div class="detail-row">
            <div class="detail-label">PLS单号</div>
            <div class="detail-value">{{ detailData.receiptNo || "-" }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-label">创建时间</div>
            <div class="detail-value">
              {{ formatTime(detailData.createTime) }}
            </div>
          </div>
          <div class="detail-row">
            <div class="detail-label">供应商</div>
            <div class="detail-value">{{ detailData.supplierName || "-" }}</div>
          </div>

          <div class="collapse-section">
            <div class="collapse-header" @click="isCollapsed = !isCollapsed">
              <span class="collapse-title">更多信息</span>
              <span class="collapse-icon">{{ isCollapsed ? "▼" : "▲" }}</span>
            </div>
            <div v-show="!isCollapsed" class="collapse-content">
              <div class="detail-row">
                <div class="detail-label">入库单号</div>
                <div class="detail-value truncate">
                  {{ detailData.wareInNo || "-" }}
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-label">入库类型</div>
                <div class="detail-value">
                  {{ getWareInTypeText(detailData.wareInType) }}
                </div>
              </div>
              <div v-if="detailData.storeName" class="detail-row">
                <div class="detail-label">仓库</div>
                <div class="detail-value">{{ detailData.storeName }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="detail-card">
          <div class="detail-card-header">
            <div class="detail-card-title">物料信息</div>
          </div>
          <div v-if="detailData.wareInDetailList?.length" class="material-list">
            <div
              v-for="detail in detailData.wareInDetailList"
              :key="detail.id"
              class="material-item"
            >
              <div class="material-row">
                <div class="material-label">物料编码</div>
                <div class="material-value">
                  {{ detail.materialNo || "-" }}
                  <el-button
                    class="location-query-btn"
                    @click="handleQueryLocation(detail)"
                    size="small"
                  >
                    查库位
                  </el-button>
                  <el-button
                    class="location-query-btn"
                    @click="handleQueryImage(detail)"
                    size="small"
                  >
                    物料图片
                  </el-button>
                </div>
              </div>
              <div class="material-row">
                <div class="material-label">物料描述</div>
                <div
                  class="material-value truncate"
                  :title="detail.materialDesc"
                >
                  {{ detail.materialDesc || "-" }}
                </div>
              </div>
              <div class="material-row">
                <div class="material-label">来货数量</div>
                <div class="material-value">
                  {{ formatQty(detail.wareInNum) }}
                </div>
              </div>
              <div v-if="detail.recordVOList?.length" class="pallet-info">
                <div class="pallet-title">归托详情</div>
                <div class="pallet-table-wrapper">
                  <table class="pallet-table">
                    <thead>
                      <tr>
                        <th>托盘码</th>
                        <th>数量</th>
                        <th>库位</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="record in detail.recordVOList"
                        :key="record.id"
                      >
                        <td>{{ record.palletCode || "-" }}</td>
                        <td>{{ formatQty(record.wareInNum) }}</td>
                        <td>{{ record.wareName || "-" }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="remainingQty > 0" class="remaining-hint">
                  剩余未归托：<span class="remaining-qty">{{
                    formatQty(remainingQty)
                  }}</span>
                </div>
                <div v-else class="remaining-hint done">已全部归托</div>
              </div>
            </div>
          </div>

          <div class="remark-section">
            <div class="remark-label" @click="toggleRemark">
              备注 {{ showRemark ? "▼" : "▲" }}
            </div>
            <el-input
              v-if="showRemark"
              v-model="remark"
              class="remark-input"
              placeholder="请输入备注信息（选填）"
              type="textarea"
              :rows="3"
            />
          </div>
        </div>

        <div v-if="!hasPalletRecords" class="scan-section">
          <div class="scan-input-area">
            <div v-if="!autoAddPallet" class="scan-input-wrapper" @click.stop>
              <el-select
                v-model="selectedPallet"
                class="scan-input"
                placeholder="扫描托盘码"
                filterable
                remote
                :remote-method="handlePalletSearch"
                :loading="palletLoading"
                clearable
                @clear="clearPalletCode"
                @change="handlePalletSelect"
              >
                <el-option
                  v-for="p in palletOptions"
                  :key="p.id"
                  :label="p.palletCode"
                  :value="p.palletCode"
                />
              </el-select>
            </div>
            <el-input
              v-else
              ref="scanInputRef"
              v-model="palletCode"
              class="scan-input"
              placeholder="扫描托盘码"
              @input="handleScanInput"
              @keydown.enter="handleScanEnter"
              @change="handleScanChange"
              :auto-focus="true"
            >
              <template #suffix>
                <el-button
                  type="text"
                  class="scan-btn"
                  @click="startScan"
                  aria-label="扫码"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    width="20"
                    height="20"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <path
                      d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z"
                    />
                  </svg>
                </el-button>
              </template>
            </el-input>
            <el-input
              v-model.number="remainingQty"
              class="qty-input"
              type="number"
              placeholder="归托数"
              @input="handleQtyInput"
              @blur="handleQtyBlur"
            />
            <el-button
              v-if="!autoAddPallet"
              type="primary"
              class="add-pallet-btn"
              @click="handleScan"
            >
              添加托盘
            </el-button>
          </div>
          <div v-if="scanHistory.length" class="scan-history">
            <div class="history-title">已扫描托盘</div>
            <div
              v-for="(item, idx) in scanHistory"
              :key="idx"
              class="history-item"
            >
              <span class="history-code">{{ item.code }}</span>
              <span class="history-qty">{{ formatQty(item.qty) }}</span>
              <el-button
                class="history-del"
                @click="removeScan(idx)"
                size="small"
                >×</el-button
              >
            </div>
          </div>
        </div>
      </template>
    </main>

    <footer class="footer" v-if="detailData">
      <el-button
        type="primary"
        size="large"
        round
        class="confirm-btn"
        :disabled="
          confirming || (!hasPalletRecords && scanHistory.length === 0)
        "
        :loading="confirming"
        @click="handleConfirm"
      >
        {{ confirming ? "收货确认中..." : "收货完成" }}
      </el-button>
    </footer>

    <!-- 库位信息弹窗 -->
    <el-dialog
      v-model="locationVisible"
      title="库位信息"
      width="90%"
      :close-on-click-modal="true"
      class="location-dialog"
    >
      <div
        v-if="locationLoading"
        v-loading="locationLoading"
        element-loading-text="加载中..."
        style="min-height: 100px"
      ></div>
      <template v-else>
        <div v-if="currentMaterialNo" class="location-material">
          <span class="location-material-label">物料编码：</span>
          <span class="location-material-value">{{ currentMaterialNo }}</span>
        </div>
        <div v-if="locationWareList.length" class="location-table-wrapper">
          <el-table
            :data="locationWareList"
            stripe
            size="small"
            row-class-name="ware-row"
            :row-style="
              ({ row }) => (row.isBad ? { backgroundColor: '#fff2f0' } : {})
            "
            @row-click="selectWare"
            style="text-align: center"
          >
            <el-table-column type="index" label="#" width="40" align="center" />
            <el-table-column prop="name" label="库位" align="center" />
            <el-table-column
              prop="stockNum"
              label="库存数量"
              width="80"
              align="center"
            />
            <el-table-column prop="wareName" label="库位名称" align="center" />
            <el-table-column label="类型" width="70" align="center">
              <template #default="{ row }">
                <el-tag
                  :type="row.isBad ? 'danger' : 'success'"
                  size="small"
                  round
                >
                  {{ row.isBad ? "不良品" : "良品" }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <el-empty v-else description="暂无库位信息" :image-size="60" />
      </template>
    </el-dialog>

    <!-- 扫码组件 -->
    <CameraScan
      v-model:visible="scanVisible"
      title="扫描托盘码"
      @success="handleScanSuccess"
      @close="scanVisible = false"
    />

    <!-- 物料图片弹窗 -->
    <el-dialog
      v-model="imageDialogVisible"
      title="物料图片"
      width="90%"
      :close-on-click-modal="true"
      class="material-image-dialog"
    >
      <div
        v-if="imageLoading"
        v-loading="imageLoading"
        element-loading-text="加载中..."
        style="min-height: 100px"
      ></div>
      <template v-else>
        <div v-if="currentImageMaterialNo" class="image-material-info">
          <span class="image-material-label">物料编码：</span>
          <span class="image-material-value">{{ currentImageMaterialNo }}</span>
        </div>
        <div v-if="formatImageUrl(materialImageUrl)" class="image-preview-area">
          <img
            :src="formatImageUrl(materialImageUrl)"
            class="material-image"
            @click="openImagePreview(formatImageUrl(materialImageUrl))"
            @error="handleImageError"
          />
          <div class="image-zoom-hint">
            <el-icon><ZoomIn /></el-icon>
            <span>点击查看大图</span>
          </div>
        </div>
        <el-empty v-else description="暂无物料图片" :image-size="80" />
      </template>
    </el-dialog>

    <!-- 图片大图预览（支持缩放、拖拽） -->
    <teleport to="body">
      <div
        v-if="showImagePreview"
        class="image-preview-overlay"
        @click.self="closeImagePreview"
      >
        <div
          class="image-preview-container"
          @dblclick="toggleZoom"
          @wheel.prevent="handleWheel"
          @touchstart.passive="handleTouchStart"
          @touchmove.passive="handleTouchMove"
          @touchend="handleTouchEnd"
          @mousedown="handleMouseDown"
        >
          <img
            :src="previewImageUrl"
            class="preview-image"
            :style="previewImageStyle"
            @dragstart.prevent
          />
        </div>

        <div class="preview-toolbar">
          <el-button circle size="small" @click="zoomOut">
            <el-icon><ZoomOut /></el-icon>
          </el-button>
          <span class="zoom-level">{{ Math.round(previewScale * 100) }}%</span>
          <el-button circle size="small" @click="zoomIn">
            <el-icon><ZoomIn /></el-icon>
          </el-button>
          <el-button circle size="small" @click="resetZoom">
            <el-icon><RefreshLeft /></el-icon>
          </el-button>
          <el-button circle size="small" @click="closeImagePreview">
            <el-icon><Close /></el-icon>
          </el-button>
        </div>

        <div class="preview-hint">双击缩放 · 双指缩放 · 拖拽移动</div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessageBox, ElMessage } from "element-plus";
import { ZoomIn, ZoomOut, Close, RefreshLeft } from "@element-plus/icons-vue";
import { getWareInReceiveDetail, collectAndConfirm } from "@/api/wareInReceive";
import { queryStockLocationByMaterialId } from "@/api/stockIn";
import { getPalletCodeList } from "@/api/stockTransfer";
import service from "@/utils/request";
import CameraScan from "@/components/CameraScan";

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const detailData = ref(null);
const palletCode = ref("");
const selectedPallet = ref(null);
const palletDropdownOpen = ref(false);
const palletLoading = ref(false);
const palletOptions = ref([]);
const palletSearchSeq = ref(0);
const confirming = ref(false);
const scanHistory = ref([]);
const showRemark = ref(false);
const scanTimeout = ref(null);
const scanInputRef = ref(null);
const isClearingInput = ref(false);
const scanVisible = ref(false);

const hasPalletRecords = ref(false);

const totalQty = ref(0);
const collectedQty = ref(0);
const remainingQty = ref(0);
const isCollapsed = ref(true);

const currentStep = computed(() => {
  if (hasPalletRecords.value) {
    return 2;
  }
  if (scanHistory.value.length > 0) {
    return 1;
  }
  return 0;
});

const autoAddPallet = ref(true);
const remark = ref("");

const supplierId = ref("");
const supplierName = ref("");

// 库位查询相关
const locationVisible = ref(false);
const locationLoading = ref(false);
const locationWareList = ref([]);
const currentMaterialNo = ref("");

const handleQueryLocation = async (detail) => {
  if (!detail.materialId) {
    ElMessage.error("缺少物料ID");
    return;
  }

  locationVisible.value = true;
  locationLoading.value = true;
  locationWareList.value = [];
  currentMaterialNo.value = detail.materialNo || "";

  try {
    const res = await queryStockLocationByMaterialId({
      materialId: detail.materialId,
      materialNo: detail.materialNo || "",
      current: 1,
      size: 20,
    });

    if (res?.success && res?.data?.records) {
      const wareList = [];
      res.data.records.forEach((item) => {
        if (
          item.materialStockDetailList &&
          item.materialStockDetailList.length
        ) {
          item.materialStockDetailList.forEach((ware) => {
            if (ware.wareName) {
              wareList.push({
                name: ware.wareName,
                wareName: ware.wareName,
                isBad: ware.isBad === 1,
                stockNum: ware.stockNum || "0",
              });
            }
          });
        }
      });
      locationWareList.value = wareList;
    } else {
      locationWareList.value = [];
    }
  } catch (err) {
    console.error("查询库位失败", err);
    ElMessage.error("查询库位失败");
    locationWareList.value = [];
  } finally {
    locationLoading.value = false;
  }
};

const selectWare = (ware) => {
  palletCode.value = ware.name || "";
  locationVisible.value = false;
  addToScanHistory();
};

// ===== 物料图片查看 =====
const imageDialogVisible = ref(false);
const imageLoading = ref(false);
const materialImageUrl = ref("");
const currentImageMaterialNo = ref("");

// 大图预览
const showImagePreview = ref(false);
const previewImageUrl = ref("");
const previewScale = ref(1);
const previewX = ref(0);
const previewY = ref(0);
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0, scale: 1, tx: 0, ty: 0 });
const touchStartDist = ref(0);
const touchStartScale = ref(1);

const MIN_SCALE = 0.5;
const MAX_SCALE = 5;

const previewImageStyle = computed(() => ({
  transform: `translate(${previewX.value}px, ${previewY.value}px) scale(${previewScale.value})`,
  transition: isDragging.value ? "none" : "transform 0.2s ease",
}));

const formatImageUrl = (url) => {
  if (!url) return "";
  let cleanUrl = url.trim().replace(/`/g, "");
  cleanUrl = cleanUrl.replace(
    "wms-zwl.cn-south-1.myhuaweicloud.com",
    "wms-zwl.obs.cn-south-1.myhuaweicloud.com",
  );
  return cleanUrl;
};

const handleImageError = (e) => {
  e.target.style.display = "none";
};

const handleQueryImage = async (detail) => {
  if (!detail.materialId) {
    ElMessage.error("缺少物料ID");
    return;
  }

  imageDialogVisible.value = true;
  imageLoading.value = true;
  materialImageUrl.value = "";
  currentImageMaterialNo.value = detail.materialNo || "";

  try {
    const res = await service.get("/uic-system/material/detail", {
      params: { id: detail.materialId },
    });
    if (res?.success && res?.data) {
      materialImageUrl.value = res.data.image || "";
    } else {
      materialImageUrl.value = "";
    }
  } catch (err) {
    console.error("查询物料图片失败", err);
    ElMessage.error("查询物料图片失败");
    materialImageUrl.value = "";
  } finally {
    imageLoading.value = false;
  }
};

const openImagePreview = (url) => {
  if (!url) {
    ElMessage.info("暂无图片");
    return;
  }
  previewImageUrl.value = url;
  previewScale.value = 1;
  previewX.value = 0;
  previewY.value = 0;
  showImagePreview.value = true;
};

const closeImagePreview = () => {
  showImagePreview.value = false;
  previewScale.value = 1;
  previewX.value = 0;
  previewY.value = 0;
};

const zoomIn = () => {
  previewScale.value = Math.min(MAX_SCALE, previewScale.value + 0.25);
};

const zoomOut = () => {
  previewScale.value = Math.max(MIN_SCALE, previewScale.value - 0.25);
};

const resetZoom = () => {
  previewScale.value = 1;
  previewX.value = 0;
  previewY.value = 0;
};

const toggleZoom = () => {
  if (previewScale.value > 1) {
    resetZoom();
  } else {
    previewScale.value = 2.5;
  }
};

const handleWheel = (e) => {
  const delta = e.deltaY > 0 ? -0.1 : 0.1;
  previewScale.value = Math.min(
    MAX_SCALE,
    Math.max(MIN_SCALE, previewScale.value + delta),
  );
};

const handleMouseDown = (e) => {
  if (previewScale.value <= 1) return;
  isDragging.value = true;
  dragStart.value = {
    x: e.clientX,
    y: e.clientY,
    scale: previewScale.value,
    tx: previewX.value,
    ty: previewY.value,
  };
  window.addEventListener("mousemove", handleMouseMove);
  window.addEventListener("mouseup", handleMouseUp);
};

const handleMouseMove = (e) => {
  if (!isDragging.value) return;
  const dx = e.clientX - dragStart.value.x;
  const dy = e.clientY - dragStart.value.y;
  previewX.value = dragStart.value.tx + dx;
  previewY.value = dragStart.value.ty + dy;
};

const handleMouseUp = () => {
  isDragging.value = false;
  window.removeEventListener("mousemove", handleMouseMove);
  window.removeEventListener("mouseup", handleMouseUp);
};

const handleTouchStart = (e) => {
  if (e.touches.length === 2) {
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    touchStartDist.value = Math.sqrt(dx * dx + dy * dy);
    touchStartScale.value = previewScale.value;
  } else if (e.touches.length === 1 && previewScale.value > 1) {
    isDragging.value = true;
    dragStart.value = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      scale: previewScale.value,
      tx: previewX.value,
      ty: previewY.value,
    };
  }
};

const handleTouchMove = (e) => {
  if (e.touches.length === 2 && touchStartDist.value > 0) {
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const newScale = Math.min(
      MAX_SCALE,
      Math.max(
        MIN_SCALE,
        touchStartScale.value * (dist / touchStartDist.value),
      ),
    );
    previewScale.value = newScale;
  } else if (e.touches.length === 1 && isDragging.value) {
    const dx = e.touches[0].clientX - dragStart.value.x;
    const dy = e.touches[0].clientY - dragStart.value.y;
    previewX.value = dragStart.value.tx + dx;
    previewY.value = dragStart.value.ty + dy;
  }
};

const handleTouchEnd = (e) => {
  if (e.touches.length < 2) {
    touchStartDist.value = 0;
    isDragging.value = false;
  }
};

const toggleRemark = () => {
  showRemark.value = !showRemark.value;
};

const formatTime = (val) => {
  if (!val) return "-";
  return String(val);
};

const formatQty = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return n.toFixed(3);
};

const getWareInTypeText = (type) => {
  const map = {
    0: "收货入库",
    1: "退货入库",
    4: "计划取消",
  };
  return map[type] || "未知";
};

const loadDetail = async () => {
  const wareInId = route.query?.wareInId || route.query?.receiptId;
  if (!wareInId) {
    ElMessage.error("缺少入库单ID");
    return;
  }

  loading.value = true;
  try {
    const res = await getWareInReceiveDetail(wareInId);
    detailData.value = res?.data;

    const details = detailData.value?.wareInDetailList || [];
    hasPalletRecords.value = details.some((d) => d.recordVOList?.length > 0);

    const detail = details[0];
    if (detail) {
      totalQty.value = Number(detail.wareInNum) || Number(detail.comeNum) || 0;

      if (hasPalletRecords.value) {
        collectedQty.value = (detail.recordVOList || []).reduce(
          (sum, record) => {
            return sum + (Number(record.wareInNum) || 0);
          },
          0,
        );
      } else {
        collectedQty.value = 0;
      }

      remainingQty.value = totalQty.value - collectedQty.value;

      // 明细备注回填到备注框（主单 remark 为空时用明细 remark）
      const detailRemark = detail.remark || detailData.value?.remark || "";
      remark.value = detailRemark;
      if (detailRemark) {
        showRemark.value = true;
      }
    }
  } catch (err) {
    console.error("加载详情失败", err);
    ElMessage.error("加载失败");
  } finally {
    loading.value = false;
  }
};

const handleScanInput = () => {
  scanTimeout.value && clearTimeout(scanTimeout.value);
};

const handleScanEnter = (e) => {
  e.preventDefault();
  e.stopPropagation();
  scanTimeout.value && clearTimeout(scanTimeout.value);
  setTimeout(() => {
    addToScanHistory();
  }, 300);
};

const handleScanChange = () => {
  scanTimeout.value && clearTimeout(scanTimeout.value);
  if (!isClearingInput.value) {
    setTimeout(() => {
      addToScanHistory();
    }, 100);
  }
};

const handleGlobalKeydown = (e) => {
  if (
    (e.key === "Enter" || e.keyCode === 13) &&
    scanInputRef.value &&
    document.activeElement === scanInputRef.value.$el.querySelector("input")
  ) {
    e.preventDefault();
    e.stopPropagation();
    setTimeout(() => {
      addToScanHistory();
    }, 300);
  }
};

const addToScanHistory = () => {
  let code = "";
  if (scanInputRef.value) {
    const inputEl = scanInputRef.value.$el.querySelector("input");
    if (inputEl) {
      code = inputEl.value.trim();
    }
  }
  if (!code) {
    code = palletCode.value.trim();
  }
  if (!code) {
    ElMessage.warning("请先输入或选择托盘码");
    return;
  }
  if (remainingQty.value <= 0) {
    ElMessage.warning("已达到来货数量上限");
    return;
  }

  const qtyToAdd = remainingQty.value;
  const existing = scanHistory.value.find((item) => item.code === code);

  if (existing) {
    const prevQty = Number(existing.qty) || 0;
    existing.qty = prevQty + qtyToAdd;
  } else {
    scanHistory.value.push({ code, qty: qtyToAdd });
  }

  collectedQty.value += qtyToAdd;
  remainingQty.value = totalQty.value - collectedQty.value;
  isClearingInput.value = true;
  palletCode.value = "";
  if (scanInputRef.value) {
    const inputEl = scanInputRef.value.$el.querySelector("input");
    if (inputEl) {
      inputEl.value = "";
    }
  }
  selectedPallet.value = null;
  palletOptions.value = [];
  ElMessage.success("已添加到扫码托盘");
  setTimeout(() => {
    isClearingInput.value = false;
  }, 500);
};

const closePalletDropdown = () => {
  palletDropdownOpen.value = false;
};

const clearPalletCode = () => {
  palletCode.value = "";
  selectedPallet.value = null;
  palletOptions.value = [];
};

const handlePalletSearch = async (keyword) => {
  const seq = palletSearchSeq.value + 1;
  palletSearchSeq.value = seq;

  if (!keyword) {
    palletOptions.value = [];
    return;
  }

  palletLoading.value = true;
  try {
    const res = await getPalletCodeList({ palletCode: keyword });
    if (seq !== palletSearchSeq.value) return;
    const data = res?.data;
    palletOptions.value = Array.isArray(data) ? data : [];
  } catch (e) {
    if (seq !== palletSearchSeq.value) return;
    palletOptions.value = [];
  } finally {
    if (seq === palletSearchSeq.value) {
      palletLoading.value = false;
    }
  }
};

const handlePalletSelect = (palletCodeStr) => {
  if (palletCodeStr) {
    palletCode.value = palletCodeStr;
  } else {
    palletCode.value = "";
  }
};

const handleQtyInput = (val) => {
  const num = Number(val);
  if (num < 0) {
    remainingQty.value = 0;
  } else if (num > totalQty.value) {
    remainingQty.value = totalQty.value;
  }
};

const handleQtyBlur = () => {
  const num = Number(remainingQty.value);
  if (isNaN(num) || num <= 0) {
    remainingQty.value = totalQty.value - collectedQty.value;
  } else if (num > totalQty.value) {
    remainingQty.value = totalQty.value;
  }
};

const handleScan = () => {
  addToScanHistory();
};

const startScan = () => {
  scanVisible.value = true;
};

const handleScanSuccess = (decodedText) => {
  const text = String(decodedText || "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[\r\n\t]/g, "")
    .trim();

  if (!text) return;

  palletCode.value = text;
  addToScanHistory();
  ElMessage.success("扫码成功");
  scanVisible.value = false;
};

const removeScan = (idx) => {
  const item = scanHistory.value[idx];
  if (item) {
    const qty = Number(item.qty) || 0;
    collectedQty.value -= qty;
    remainingQty.value = totalQty.value - collectedQty.value;
  }
  scanHistory.value.splice(idx, 1);
};

const handleConfirm = async () => {
  if (!detailData.value) return;

  const detail = detailData.value.wareInDetailList?.[0];
  if (!detail) {
    ElMessage.error("缺少物料详情");
    return;
  }

  const totalQty = Number(detail.wareInNum) || Number(detail.comeNum) || 0;
  let collectedQty = 0;

  if (hasPalletRecords.value) {
    collectedQty = (detail.recordVOList || []).reduce((sum, record) => {
      return sum + (Number(record.wareInNum) || 0);
    }, 0);
  } else {
    collectedQty = scanHistory.value.reduce((sum, item) => {
      return sum + (Number(item.qty) || 0);
    }, 0);
  }

  const remaining = totalQty - collectedQty;

  let confirmMsg = "";
  if (remaining > 0) {
    confirmMsg = `已入库 ${collectedQty} 个，还剩 ${remaining} 个未入库，确定要继续入库吗？`;
  } else {
    confirmMsg = `已全部入库完成，确定要确认入库吗？`;
  }

  try {
    await ElMessageBox.confirm(confirmMsg, "确认", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
      center: true,
      customClass: "warein-single-confirm-box",
      confirmButtonClass: "warein-single-confirm-ok",
      cancelButtonClass: "warein-single-confirm-cancel",
    });
    await doConfirm();
  } catch {
    ElMessage.info("已取消");
  }
};

const doConfirm = async () => {
  if (!detailData.value) return;

  confirming.value = true;
  try {
    const detail = detailData.value.wareInDetailList?.[0];
    if (!detail) {
      ElMessage.error("缺少物料详情");
      return;
    }

    let wareInDetailRecordVOList = [];

    if (hasPalletRecords.value) {
      wareInDetailRecordVOList = (detail.recordVOList || []).map((record) => ({
        palletCode: record.palletCode || "",
        wareInNum: Number(record.wareInNum) || 0,
      }));
    } else {
      wareInDetailRecordVOList = scanHistory.value.map((item) => ({
        palletCode: item.code,
        wareInNum: Number(item.qty),
      }));
    }

    const params = {
      wareInId: String(route.query?.wareInId),
      materialId: String(detail.materialId),
      type: 0,
      wareInDetailRecordVOList,
      remark: remark.value?.trim() || "",
    };

    const res = await collectAndConfirm(params);
    if (res?.success) {
      ElMessage.success("正在收货中");
      setTimeout(() => {
        let url = "/warein-receive?status=pending";
        if (supplierId.value) url += `&supplierId=${supplierId.value}`;
        if (supplierName.value)
          url += `&supplierName=${encodeURIComponent(supplierName.value)}`;
        const nk = route.query?.noKeyword;
        const ns = route.query?.noSearchType;
        if (nk) url += `&noKeyword=${nk}`;
        if (ns) url += `&noSearchType=${ns}`;
        router.push(url);
      }, 1500);
    } else {
      const errorMsg =
        res?.message || res?.msg || res?.data?.message || "收货失败";
      ElMessage.error(errorMsg);
    }
  } catch (err) {
    console.error("收货确认失败", err);
    let errorMsg = "收货失败";
    if (err?.response?.data?.message) {
      errorMsg = err.response.data.message;
    } else if (err?.response?.data?.msg) {
      errorMsg = err.response.data.msg;
    } else if (err?.message) {
      errorMsg = err.message;
    }
    ElMessage.error(errorMsg);
  } finally {
    confirming.value = false;
  }
};

const handleBack = () => {
  let backPath = route.query?.back || "/warein-receive?status=pending";
  if (backPath === "/warein-receive?status=pending" && !route.query?.back) {
    const supplierId = route.query?.supplierId;
    const supplierName = route.query?.supplierName;
    if (supplierId) backPath += `&supplierId=${supplierId}`;
    if (supplierName)
      backPath += `&supplierName=${encodeURIComponent(supplierName)}`;
    const nk = route.query?.noKeyword;
    const ns = route.query?.noSearchType;
    if (nk) backPath += `&noKeyword=${nk}`;
    if (ns) backPath += `&noSearchType=${ns}`;
  }
  // 返回 PLS 扫码页时用 replace，避免与收货页互相跳转
  if (String(backPath).includes("/pls-receive")) {
    router.replace(String(backPath));
    return;
  }
  router.push(backPath);
};

onMounted(() => {
  try {
    const savedWarehouseSettings = localStorage.getItem("warehouseSettings");
    if (savedWarehouseSettings) {
      const settings = JSON.parse(savedWarehouseSettings);
      autoAddPallet.value = settings.autoAddPallet ?? true;
    }
  } catch (e) {
    console.warn("无法读取仓库设置，使用默认值", e);
    autoAddPallet.value = true;
  }

  supplierId.value = route.query?.supplierId || "";
  supplierName.value = route.query?.supplierName || "";

  loadDetail();

  document.addEventListener("keydown", handleGlobalKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleGlobalKeydown);
  scanTimeout.value && clearTimeout(scanTimeout.value);
});
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  background: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  padding-bottom: 80px;
}

.page-container,
.page-container * {
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
}

.back-btn {
  border: none;
  background: transparent;
  padding: 6px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.icon-back {
  width: 20px;
  height: 20px;
}

.title {
  font-size: 16px;
  font-weight: 600;
}

.header-right {
  width: 36px;
}

.header-placeholder {
  width: 100%;
}

.content {
  padding: 8px 10px;
  margin-top: calc(52px + env(safe-area-inset-top));
}

.step-card {
  background: var(--card-background);
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-card :deep(.el-steps) {
  padding: 0;
  width: 100%;
  max-width: 340px;
}

.step-card :deep(.el-step) {
  flex: 1;
}

.step-card :deep(.el-step__title) {
  font-size: 11px;
}

.step-card :deep(.el-step.is-active .el-step__title) {
  font-weight: 600;
}

.step-card :deep(.el-step.is-finish .el-step__title) {
  font-weight: 600;
}

.step-card :deep(.el-step__icon) {
  width: 14px;
  height: 14px;
  line-height: 14px;
  font-size: 9px;
}

.detail-card {
  background: var(--card-background);
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid var(--border-color);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-size: 13px;
  color: var(--text-secondary);
}

.detail-value {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
  text-align: right;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-card-header {
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border-color);
}

.detail-card-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: center;
}

.material-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.material-item {
  padding: 0;
  background: var(--card-background);
}

.material-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
}

.material-label {
  font-size: 13px;
  color: var(--text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
  margin-right: 8px;
}

.material-value {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
  flex: 1;
  text-align: right;
  min-width: 0;
}

.material-value.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pallet-info {
  background: var(--card-background);
  padding: 6px 8px;
  margin-top: 0;
}

.pallet-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
  text-align: center;
}

.pallet-table-wrapper {
  overflow-x: auto;
}

.pallet-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  min-width: 260px;
}

.pallet-table thead tr {
  background: var(--background-color);
}

.pallet-table th {
  padding: 6px;
  text-align: center;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 11px;
  border-bottom: 1px solid var(--border-color);
}

.pallet-table td {
  padding: 6px;
  text-align: center;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.pallet-table tbody tr:last-child td {
  border-bottom: none;
}

.remaining-hint {
  margin-top: 6px;
  padding: 6px 8px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 6px;
  color: #ef4444;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}

.remaining-hint .remaining-qty {
  font-size: 14px;
  font-weight: 700;
}

.remaining-hint.done {
  background: rgba(16, 185, 129, 0.08);
  border-color: rgba(16, 185, 129, 0.2);
  color: #10b981;
}

.remark-section {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border-color);
}

.remark-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.remark-input {
  width: 100%;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.collapse-section {
  margin-top: 4px;
}

.collapse-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  cursor: pointer;
}

.collapse-title {
  font-size: 11px;
  color: var(--text-secondary);
}

.collapse-icon {
  font-size: 9px;
  color: var(--text-secondary);
  margin-left: 4px;
}

.collapse-content {
  padding-top: 4px;
}

.scan-section {
  background: var(--card-background);
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
}

.scan-input-area {
  display: flex;
  gap: 6px;
  align-items: center;
}

.scan-input-wrapper {
  flex: 1;
  min-width: 0;
}

.scan-input {
  width: 100%;
}

.scan-input :deep(.el-select__wrapper),
.scan-input :deep(.el-input__wrapper) {
  height: 44px;
  border-radius: 8px;
}

.scan-input :deep(.el-select__inner),
.scan-input :deep(.el-input__inner) {
  height: 44px;
  line-height: 44px;
  font-size: 15px;
}

.scan-input :deep(.el-input__suffix) {
  pointer-events: auto;
}

.scan-btn {
  padding: 0;
  margin-right: 4px;
  color: var(--primary-color);
}

.scan-btn:hover {
  color: var(--primary-color);
}

.add-pallet-btn {
  height: 44px;
  padding: 0 14px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  white-space: nowrap;
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
}

.qty-input {
  width: 88px;
}

.qty-input :deep(.el-input__wrapper) {
  height: 44px;
  border-radius: 8px;
}

.qty-input :deep(.el-input__inner) {
  height: 44px;
  line-height: 44px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  -moz-appearance: textfield;
}

.qty-input :deep(.el-input__inner::-webkit-inner-spin-button),
.qty-input :deep(.el-input__inner::-webkit-outer-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

.scan-history {
  margin-top: 10px;
}

.history-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 6px;
}

.history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: var(--background-color);
  border-radius: 6px;
  margin-bottom: 4px;
}

.history-item:last-child {
  margin-bottom: 0;
}

.history-code {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
}

.history-qty {
  font-size: 13px;
  color: var(--primary-color);
  font-weight: 600;
}

.history-del {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--border-color);
  border: none;
  border-radius: 50%;
  font-size: 16px;
  color: var(--text-secondary);
  cursor: pointer;
}

.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 12px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  background: var(--card-background);
  box-shadow: 0 -2px 8px var(--shadow-light);
}

.footer .confirm-btn {
  width: 100%;
  height: 48px;
  font-size: 17px;
  font-weight: 600;
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
}

.location-query-btn {
  padding: 4px 10px;
  font-size: 13px;
  height: 28px;
  margin-left: 6px;
}

.location-dialog :deep(.el-dialog) {
  border-radius: 12px;
}

.location-dialog :deep(.el-dialog__header) {
  padding: 12px 16px;
  margin: 0;
  border-bottom: 1px solid var(--border-color);
}

.location-dialog :deep(.el-dialog__title) {
  font-size: 15px;
  font-weight: 600;
}

.location-dialog :deep(.el-dialog__body) {
  padding: 12px;
}

.location-material {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  background: var(--background-color);
  border-radius: 6px;
  margin-bottom: 10px;
  font-size: 13px;
}

.location-material-label {
  color: var(--text-secondary);
  margin-right: 6px;
}

.location-material-value {
  color: var(--text-primary);
  font-weight: 600;
}

.location-table-wrapper {
  max-height: 400px;
  overflow-y: auto;
}

/* ===== 物料图片弹窗 ===== */
.material-image-dialog :deep(.el-dialog) {
  border-radius: 12px;
}

.material-image-dialog :deep(.el-dialog__header) {
  padding: 12px 16px;
  margin: 0;
  border-bottom: 1px solid var(--border-color);
}

.material-image-dialog :deep(.el-dialog__title) {
  font-size: 15px;
  font-weight: 600;
}

.material-image-dialog :deep(.el-dialog__body) {
  padding: 12px;
}

.image-material-info {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  background: var(--background-color);
  border-radius: 6px;
  margin-bottom: 10px;
  font-size: 13px;
}

.image-material-label {
  color: var(--text-secondary);
  margin-right: 6px;
}

.image-material-value {
  color: var(--text-primary);
  font-weight: 600;
}

.image-preview-area {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: var(--background-color);
}

.material-image {
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  cursor: zoom-in;
  display: block;
}

.image-zoom-hint {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 12px;
  color: #fff;
  font-size: 11px;
}

.image-zoom-hint .el-icon {
  font-size: 12px;
}

/* ===== 图片大图预览 ===== */
.image-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  overflow: hidden;
  touch-action: none;
}

.image-preview-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  -webkit-user-drag: none;
}

.image-preview-container:active {
  cursor: grabbing;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  pointer-events: none;
  -webkit-user-drag: none;
}

.preview-toolbar {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.zoom-level {
  font-size: 13px;
  color: #fff;
  min-width: 48px;
  text-align: center;
  font-weight: 500;
}

.preview-hint {
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  white-space: nowrap;
}

@media (max-width: 480px) {
  .preview-toolbar {
    bottom: 32px;
    gap: 8px;
    padding: 6px 12px;
  }

  .preview-hint {
    bottom: 88px;
    font-size: 11px;
  }
}
</style>

<style>
/* MessageBox 挂载到 body，需非 scoped */
.warein-single-confirm-box {
  width: min(86vw, 320px) !important;
  padding-bottom: 8px;
}

.warein-single-confirm-box .el-message-box__title {
  font-size: 18px;
}

.warein-single-confirm-box .el-message-box__message {
  font-size: 16px;
  line-height: 1.6;
  padding: 8px 0 4px;
}

.warein-single-confirm-box .el-message-box__btns {
  display: flex;
  gap: 12px;
  padding: 12px 16px 16px;
}

.warein-single-confirm-box .warein-single-confirm-cancel,
.warein-single-confirm-box .warein-single-confirm-ok {
  flex: 1;
  margin: 0 !important;
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
}
</style>
